<template>
  <form @submit.prevent="handleSubmit" id="company_edit_form" enctype="multipart/form-data">

    <!-- Company Name -->
    <div class="mb-4">
      <label class="form-label form-label-thin">
        Company Name <span class="text-danger">*</span>
      </label>
      <input
        type="text"
        class="form-control form-control-thin"
        id="name"
        name="name"
        ref="name"
        v-model="form.name"
        placeholder="Company Name"
      />
    </div>

    <!-- Email -->
    <div class="mb-4">
      <label class="form-label form-label-thin">
        Email <span class="text-danger">*</span>
      </label>
      <input
        type="email"
        class="form-control form-control-thin"
        id="email"
        name="email"
        ref="email"
        v-model="form.email"
        placeholder="company@example.com"
      />
    </div>

    <!-- Phone -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Phone</label>
      <input
        type="text"
        class="form-control form-control-thin"
        id="phone"
        name="phone"
        ref="phone"
        v-model="form.phone"
        placeholder="Phone Number"
      />
    </div>

    <!-- Points of Contact -->
    <div class="row mb-4">
      <div class="col-6">
        <label class="form-label form-label-thin">Point of Contact 1</label>
        <input
          type="text"
          class="form-control form-control-thin"
          id="poc1"
          name="poc1"
          ref="poc1"
          v-model="form.poc1"
          placeholder="POC 1 Name"
        />
      </div>
      <div class="col-6">
        <label class="form-label form-label-thin">Point of Contact 2</label>
        <input
          type="text"
          class="form-control form-control-thin"
          id="poc2"
          name="poc2"
          ref="poc2"
          v-model="form.poc2"
          placeholder="POC 2 Name"
        />
      </div>
    </div>

    <!-- Address -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Address</label>
      <input
        type="text"
        class="form-control form-control-thin"
        v-model="form.address"
        id="address"
        ref="address"
        name="address"
        placeholder="Street / Building"
      />
    </div>

    <!-- Country & City -->
    <div class="row mb-4">
      <div class="col-6">
        <label class="form-label form-label-thin">Country</label>
        <input
          type="text"
          class="form-control form-control-thin"
          v-model="form.country"
          id="country"
          ref="country"
          name="country"
          placeholder="Country"
        />
      </div>
      <div class="col-6">
        <label class="form-label form-label-thin">City</label>
        <input
          type="text"
          class="form-control form-control-thin"
          v-model="form.city"
          id="city"
          name="city"
          ref="city"
          placeholder="City"
        />
      </div>
    </div>

    <!-- Status -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Status</label>
      <select
        class="form-control form-control-thin"
        v-model="form.status"
        id="status"
        ref="status"
        name="status"
      >
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>

    <!-- Logo -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Logo</label>

      <!-- Existing logo -->
      <div v-if="form.existing_logo && !form.logo_preview" class="mb-2 d-flex align-items-center gap-3">
        <img
          :src="form.existing_logo"
          alt="Current Logo"
          style="width:60px;height:60px;border-radius:8px;object-fit:cover;border:1px solid #dee2e6;"
        />
        <div>
          <p class="mb-1 text-muted" style="font-size:12px;">Current logo</p>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger py-0 px-2"
            style="font-size:11px;     padding: 5px !important;"
            @click="removeLogo"
          >
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>

      <input
        type="file"
        class="form-control form-control-thin"
        @change="handleLogoChange"
        accept="image/*"
        ref="logoInput"
      />

      <!-- New logo preview -->
      <div v-if="form.logo_preview" class="mt-2 d-flex align-items-center gap-3">
        <img
          :src="form.logo_preview"
          alt="New Logo Preview"
          style="width:60px;height:60px;border-radius:8px;object-fit:cover;border:1px solid #dee2e6;"
        />
        <div>
          <p class="mb-1 text-muted" style="font-size:12px;">New logo (not saved yet)</p>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary py-0 px-2"
            style="font-size:11px;"
            @click="clearNewLogo"
          >
            <i class="fa fa-times"></i> Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="close">
        <i class="fa fa-times"></i> Cancel
      </button>
      <button
        type="submit"
        class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn"
        :disabled="submitting || loadingData"
      >
        <i class="fa fa-save"></i>
        {{ submitting ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>

  </form>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'EditCompany',

  inject: ['formGuard'],

  emits: ['updated', 'close'],

  props: {
    companyId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loadingData: false,
      submitting:  false,
      removingLogo: false,

      form: {
        name:          '',
        email:         '',
        phone:         '',
        poc1:          '',
        poc2:          '',
        address:       '',
        country:       '',
        city:          '',
        status:        'active',
        logo:          null,
        logo_preview:  null,
        existing_logo: null,   // URL of the currently saved logo
        remove_logo:   false,  // tell the backend to delete the logo
      },
    }
  },

  watch: {
    form: {
      deep: true,
      handler() {
        this.formGuard.markDirty()
      },
    },
  },

  mounted() {
    this.fetchCompany()
  },

  methods: {

    /* ── Fetch existing company data ──────────────────────── */
    async fetchCompany() {
      this.loadingData = true
      const result = await form_api_submit({
        url:                `companies/get/${this.companyId}`,
        method:             'GET',
        show_success_toast: false,
        show_error_toast:   true,
        remove_pre_toasts:  false,
      })

      if (result.success) {
        const c = result.data
        this.form.name          = c.name          ?? ''
        this.form.email         = c.email         ?? ''
        this.form.phone         = c.phone         ?? ''
        this.form.poc1          = c.poc1          ?? ''
        this.form.poc2          = c.poc2          ?? ''
        this.form.address       = c.address       ?? ''
        this.form.country       = c.country       ?? ''
        this.form.city          = c.city          ?? ''
        this.form.status        = c.status        ?? 'active'
        this.form.existing_logo = c.logo_url      ?? null   // full URL from backend
        // mark clean — we haven't touched anything yet
        this.$nextTick(() => this.formGuard.markClean())
      }

      this.loadingData = false
    },

    /* ── Logo helpers ─────────────────────────────────────── */
    handleLogoChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.form.logo        = file
      this.form.remove_logo = false
      const reader = new FileReader()
      reader.onload = (e) => { this.form.logo_preview = e.target.result }
      reader.readAsDataURL(file)
    },

    clearNewLogo() {
      this.form.logo         = null
      this.form.logo_preview = null
      if (this.$refs.logoInput) this.$refs.logoInput.value = ''
    },

    removeLogo() {
      this.form.existing_logo = null
      this.form.remove_logo   = true
      this.clearNewLogo()
    },

    /* ── Submit ───────────────────────────────────────────── */
    async handleSubmit() {
      this.submitting = true

      try {
        const fd = new FormData()
        fd.append('_method', 'POST')   // Laravel method spoofing

        const scalars = ['name', 'email', 'phone', 'poc1', 'poc2', 'address', 'country', 'city', 'status']
        scalars.forEach(key => {
          fd.append(key, this.form[key] ?? '')
        })

        if (this.form.logo) {
          fd.append('logo', this.form.logo)
        }

        if (this.form.remove_logo) {
          fd.append('remove_logo', '1')
        }

        const result = await form_api_submit({
          url:            `companies/update/${this.companyId}`,
          method:         'POST',   // POST with _method=PUT for FormData
          data:           fd,
          curr_form_id:   '#company_edit_form',
        })

        if (result.success) {
          this.formGuard.markClean()
          this.$emit('updated')
          this.$emit('close')
        }
      } finally {
        this.submitting = false
      }
    },

    close() {
      this.formGuard.requestClose()
    },
  },
}
</script>